import {
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo, ReactNode } from "react";
import { matchPath, Navigate } from "react-router-dom";
import { returnsClaimsAnalyticsRoutes } from "../../../analytics/returns-claims/route";
import { registrationRoutes } from "../../../registration/route";
import {
  claimRoutes,
  fraudRoutes,
  recycleRoutes,
  returnRoutes,
  warrantyRoutes,
  withdrawalRoutes,
} from "../../../return/route";

const reportBreadcrumb = breadcrumbSlot();

const RETURNS_ROUTE_BASE = "/stores/:storeId/returns";

const HIDE_PADDING_ROUTES: string[] = [
  "/returns/*",
  "/registrations/*",
  "/claims/*",
  "/warranties/*",
  "/recycle/*",
  "/withdrawals/*",
  "/fraud/*",
  "/analytics/returns-claims",
  "/analytics/returns-claims/*",
  "/analytics/managed-returns",
  "/analytics/reports",
  "/analytics/reports/*",
];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`${RETURNS_ROUTE_BASE}${route}`, url),
  );
}

const ManagedReturnsAccessGuard = memo(function ManagedReturnsAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isManagedReturnsEnabled = team.settings.redoManagedReturns?.enabled;

  if (!isManagedReturnsEnabled) {
    return <Navigate replace to={routes.returnsHref} />;
  }

  return <>{children}</>;
});

export const returnsRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Returns" },
    async lazy() {
      const { Page } = await import("../../page");
      return { element: <Page hidePadding={hidePadding} /> };
    },
    children: [
      { index: true, element: <Navigate replace to="returns" /> },
      {
        path: "warranties",
        children: warrantyRoutes,
        handle: { permission: [ReturnsAndClaimsPermissions.MANAGE_WARRANTY] },
      },
      {
        path: "returns",
        children: returnRoutes,
        handle: { permission: [ReturnsAndClaimsPermissions.VIEW_RETURN] },
      },
      {
        path: "claims",
        children: claimRoutes,
        handle: {
          permission: [
            ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
            ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
          ],
        },
      },
      {
        path: "registrations",
        children: registrationRoutes,
        handle: { permission: [ReturnsAndClaimsPermissions.MANAGE_WARRANTY] },
      },
      {
        path: "recycle",
        children: recycleRoutes,
        handle: {
          permission: [ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS],
        },
      },
      {
        path: "withdrawals",
        children: withdrawalRoutes,
        handle: {
          permission: [ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS],
        },
      },
      {
        path: "fraud",
        children: fraudRoutes,
        handle: { permission: [ReturnsAndClaimsPermissions.VIEW_RETURN] },
      },
      {
        path: "analytics/returns-claims",
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: returnsClaimsAnalyticsRoutes,
      },
      {
        path: "analytics/managed-returns",
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: [
          {
            index: true,
            handle: { breadcrumb: "Managed Returns" },
            async lazy() {
              const { RedoManagedReturnsPage } =
                await import("@redotech/redo-merchant-managed-returns-app/redo-managed-returns-page");
              return {
                Component: () => {
                  return (
                    <ManagedReturnsAccessGuard>
                      <RedoManagedReturnsPage />
                    </ManagedReturnsAccessGuard>
                  );
                },
              };
            },
          },
        ],
      },
      {
        path: "analytics/reports",
        handle: {
          breadcrumb: "Reports",
          permission: OrderManagementPermissions.VIEW_REPORTS,
        },
        children: [
          {
            index: true,
            handle: {
              breadcrumb: reportBreadcrumb,
              permission: OrderManagementPermissions.VIEW_REPORTS,
            },
            async lazy() {
              const { ReportPage } =
                await import("../../../analytics/reports/report");
              return { element: <ReportPage breadcrumb={reportBreadcrumb} /> };
            },
          },
          {
            path: "custom",
            handle: {
              breadcrumb: "Custom reports",
              permission: OrderManagementPermissions.VIEW_REPORTS,
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { CustomAnalyticsReportsRoute } =
                    await import("../../../analytics/reports/custom/custom-analytics-reports-route");
                  return { element: <CustomAnalyticsReportsRoute /> };
                },
              },
              {
                path: ":customReportId",
                async lazy() {
                  const { CustomAnalyticsReportPage } =
                    await import("../../../analytics/reports/custom/custom-analytics-report-page");
                  return { element: <CustomAnalyticsReportPage /> };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
